'use client';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { formatDocumentNumber } from '@/utils/formatters/format-document-number';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { authStore } from '@/store/auth.store';

export enum PatientType {
  HOLDER = 'titular',
  DEPENDENT = 'dependente',
}

interface PatientOption {
  id: string;
  name: string;
  documentNumber: string;
  type: PatientType;
}

interface SearchCardPatientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPatient: (type: PatientType) => void;
}

export function SearchCardPatientModal({
  open,
  onOpenChange,
  onSelectPatient,
}: SearchCardPatientModalProps) {
  const user = useStore(authStore, (state) => state.authenticatedUser);

  const patients: PatientOption[] = user
    ? [
        {
          id: user.id ?? '',
          name: user.socialName || user.legalName,
          documentNumber: user.documentNumber,
          type: PatientType.HOLDER,
        },
        ...(user.dependents ?? []).map((dependent): PatientOption => ({
          id: dependent.id,
          name: dependent.socialName || dependent.legalName,
          documentNumber: dependent.documentNumber,
          type: PatientType.DEPENDENT,
        })),
      ]
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="center-v2"
        className="md:max-w-[640px] p-0"
        hasHeader
        data-testid={DataTestIdEnum.SEARCH_CARD_PATIENT_MODAL}
      >
        <div className="flex flex-col gap-5 p-6">
          <div className="flex items-start justify-between gap-2">
            <Typography variant={TypographyVariant.MODAL_TITLE}>Escolha o paciente</Typography>
            <SheetClose
              asChild
              data-testid={DataTestIdEnum.SEARCH_CARD_PATIENT_MODAL_CLOSE}
            >
              <button
                type="button"
                aria-label="Fechar"
                className="size-8 flex items-center justify-center text-gray-500 hover:text-gray-900 cursor-pointer"
              >
                <Icon icon={faTimes} size="2xl" />
              </button>
            </SheetClose>
          </div>

          <Typography variant={TypographyVariant.BODY_TEXT} className="text-gray-700">
            O agendamento deve ser feito em nome de quem será atendido.
          </Typography>

          <div className="flex flex-col gap-4">
            {patients.map((patient) => (
              <Button
                key={`${patient.id}-${patient.documentNumber}`}
                variant="secondary"
                color="primary"
                className="flex flex-col gap-1 py-3"
                data-testid={`${DataTestIdEnum.SEARCH_CARD_PATIENT_OPTION}-${patient.type}`}
                onClick={() => onSelectPatient(patient.type)}
              >
                <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold text-inherit text-lg">
                  {formatToTitleCase(patient.name)}
                </Typography>
                <Typography variant={TypographyVariant.BODY_TEXT} className="text-inherit text-lg">
                  {formatDocumentNumber(patient.documentNumber)}
                </Typography>
              </Button>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
